// StatusBreakdownCard's PNG export: the card AS IT LOOKS ON SCREEN — its title, the segmented bar
// and the two-column legend of tiles with percentage chips — redrawn as ECharts `graphic` elements
// (rendered and downloaded by @rdrive/helper-chart-export's `downloadOptionAsPNG`).
//
// Geometry comes from the live card: `measureStatusBreakdownCard` reads the bounding boxes of the
// title, every bar segment's `v-sparkline` svg and every legend tile (with its dot, text and chip),
// so the PNG follows the card's real width and breakpoints. The bars inside a segment are recomputed
// with Vuetify's own VBarline geometry (`vuetify/lib/components/VSparkline/VBarline.mjs`: n bars of
// `lineWidth` on a `width × height` viewBox, `gridX = max(n × lineWidth, width) / n`, each bar centred
// in its grid cell, corner radius `smooth`), scaled the way the svg is, and clipped to the svg's box.
// Colours are NEVER read from the DOM: the card, the page and the text come from @rdrive/theme
// (`tokens.palette[mode]`, `chartTheme(mode).fontColor` = Vuetify's on-surface), the segments, dots
// and chips from the items; the opacities are Vuetify 3's theme defaults (below). The hover /
// selection dimming is not exported — every status in full colour.
import { chartTheme, tokens, THEME_DARK } from '@rdrive/theme';

// Vuetify 3's theme variables (vuetify/lib/composables/theme.mjs): the card's text colour is
// on-surface at the high-emphasis opacity; a legend tile is on-surface at 0.05 (StatusBreakdownCard's
// style.css); a tonal chip's underlay is its colour at the activated opacity.
const HIGH_EMPHASIS = { light: 0.87, dark: 1 };
const TILE_OPACITY = 0.05;
const CHIP_OPACITY = 0.12;
// Vuetify's $body-font-family; the card's text classes: text-body-1 (title), text-body-2 (legend),
// the percentage chip's 0.75rem (style.css)
const FONT_FAMILY = 'Roboto, sans-serif';
const FONT_TITLE = `400 16px ${FONT_FAMILY}`;
const FONT_LEGEND = `400 14px ${FONT_FAMILY}`;
const FONT_CHIP = `400 12px ${FONT_FAMILY}`;
// rounded="lg" (card and tiles), a label chip's corners, the page margin around the card
const CARD_RADIUS = 8;
const TILE_RADIUS = 8;
const CHIP_RADIUS = 4;
const MARGIN = 16;

const box = (el, base) => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height };
};

/**
 * The live card's geometry, relative to the card's top-left corner (DOM layout only — no colours).
 * Null when the card is not rendered (loading skeleton) or has no size.
 *
 * @param {HTMLElement|null} rootEl the StatusBreakdownCard's root element
 * @returns {{ width: number, height: number, title: object|null, segments: object[],
 *   tiles: Array<{ box: object, dot: object|null, text: object|null, chip: object|null }> }|null}
 */
export function measureStatusBreakdownCard(rootEl) {
  const card = rootEl?.querySelector?.('.v-card');
  if (!card) return null;
  const base = card.getBoundingClientRect();
  if (!base.width || !base.height) return null;
  return {
    width: base.width,
    height: base.height,
    title: box(card.querySelector('.text-body-1'), base),
    segments: [...card.querySelectorAll('.chart-segment')].map((el) => box(el, base)),
    tiles: [...card.querySelectorAll('.legend-items > .v-list-item')].map((tile) => ({
      box: box(tile, base),
      dot: box(tile.querySelector('.v-avatar'), base),
      text: box(tile.querySelector('.v-list-item-title span') || tile.querySelector('.v-list-item-title'), base),
      chip: box(tile.querySelector('.v-chip'), base),
    })),
  };
}

// One segment's bars, in canvas px: Vuetify's VBarline geometry, scaled like the svg
// (preserveAspectRatio "xMidYMid meet") and clipped to the svg's box
function segmentBars(segment, rect, ox, oy) {
  const n = segment.modelValue.length;
  const lineWidth = Number(segment.lineWidth) || 4;
  const vbWidth = Number(segment.width);
  const vbHeight = Number(segment.height);
  if (!n || !vbWidth || !vbHeight || !rect?.w || !rect?.h) return [];

  const scale = Math.min(rect.w / vbWidth, rect.h / vbHeight);
  const left = ox + rect.x + (rect.w - vbWidth * scale) / 2;
  const top = oy + rect.y + (rect.h - vbHeight * scale) / 2;
  const gridX = Math.max(n * lineWidth, vbWidth) / n;
  const offsetX = n > 1 ? (gridX - lineWidth) / 2 : 0;
  const clipLeft = ox + rect.x;
  const clipRight = ox + rect.x + rect.w;

  const bars = [];
  for (let i = 0; i < n; i++) {
    const x0 = Math.max(clipLeft, left + (i * gridX + offsetX) * scale);
    const x1 = Math.min(clipRight, left + (i * gridX + offsetX + lineWidth) * scale);
    if (x1 - x0 < 0.5) continue;
    const width = x1 - x0;
    bars.push({
      x: x0,
      y: top,
      width,
      height: vbHeight * scale,
      r: Math.min(Number(segment.smooth) * scale || 0, width / 2),
    });
  }
  return bars;
}

/**
 * The card as an ECharts option of `graphic` elements, on a canvas of the card's size plus a page
 * margin — PURE (no DOM), so it renders in Node (ECharts SSR) too.
 *
 * @param {object} args
 * @param {ReturnType<typeof measureStatusBreakdownCard>} args.layout the measured card
 * @param {Array<{ color: string, modelValue: number[], width: number, height: number,
 *   lineWidth: number, smooth: number }>} args.segments the card's `segments` (the v-sparkline props)
 * @param {Array<{ title: string, color: string, value: number }>} args.items the drawn items
 *   (value > 0), in legend order
 * @param {string} args.title the card title
 * @param {string} [args.mode='light'] 'light' | 'dark'
 * @returns {{ option: object, width: number, height: number }}
 */
export function buildStatusBreakdownOption({ layout, segments, items, title, mode = 'light' }) {
  const key = mode === THEME_DARK ? 'dark' : 'light';
  const palette = tokens.palette[key];
  const { fontColor } = chartTheme(key);
  const textOpacity = HIGH_EMPHASIS[key];
  const ox = MARGIN;
  const oy = MARGIN;
  const elements = [];

  // the card
  elements.push({
    type: 'rect',
    shape: { x: ox, y: oy, width: layout.width, height: layout.height, r: CARD_RADIUS },
    style: { fill: palette.surface },
  });

  // the title
  if (layout.title && title) {
    elements.push({
      type: 'text',
      style: {
        text: title,
        x: ox + layout.title.x,
        y: oy + layout.title.y + layout.title.h / 2,
        font: FONT_TITLE,
        fill: fontColor,
        opacity: textOpacity,
        align: 'left',
        verticalAlign: 'middle',
        width: Math.max(0, layout.width - layout.title.x - 64),
        overflow: 'truncate',
      },
    });
  }

  // the segmented bar
  segments.forEach((segment, i) => {
    const color = items[i]?.color ?? segment.color;
    segmentBars(segment, layout.segments[i], ox, oy).forEach((bar) => {
      elements.push({
        type: 'rect',
        shape: { x: bar.x, y: bar.y, width: bar.width, height: bar.height, r: bar.r },
        style: { fill: color },
      });
    });
  });

  // the legend tiles
  layout.tiles.forEach((tile, i) => {
    const item = items[i];
    if (!item || !tile.box) return;
    const cy = oy + tile.box.y + tile.box.h / 2;
    elements.push({
      type: 'rect',
      shape: { x: ox + tile.box.x, y: oy + tile.box.y, width: tile.box.w, height: tile.box.h, r: TILE_RADIUS },
      style: { fill: fontColor, opacity: TILE_OPACITY },
    });
    if (tile.dot) {
      elements.push({
        type: 'circle',
        shape: { cx: ox + tile.dot.x + tile.dot.w / 2, cy: oy + tile.dot.y + tile.dot.h / 2, r: tile.dot.w / 2 },
        style: { fill: item.color },
      });
    }
    const chipLeft = tile.chip ? ox + tile.chip.x : ox + tile.box.x + tile.box.w - 8;
    if (tile.text) {
      elements.push({
        type: 'text',
        style: {
          text: item.title,
          x: ox + tile.text.x,
          y: cy,
          font: FONT_LEGEND,
          fill: fontColor,
          opacity: textOpacity,
          align: 'left',
          verticalAlign: 'middle',
          width: Math.max(0, chipLeft - (ox + tile.text.x) - 8),
          overflow: 'truncate',
        },
      });
    }
    if (tile.chip) {
      const chipCy = oy + tile.chip.y + tile.chip.h / 2;
      elements.push({
        type: 'rect',
        shape: { x: chipLeft, y: oy + tile.chip.y, width: tile.chip.w, height: tile.chip.h, r: CHIP_RADIUS },
        style: { fill: fontColor, opacity: CHIP_OPACITY * textOpacity },
      });
      elements.push({
        type: 'text',
        style: {
          text: `${Number(item.value).toFixed(1)}%`,
          x: chipLeft + tile.chip.w / 2,
          y: chipCy,
          font: FONT_CHIP,
          fill: fontColor,
          opacity: textOpacity,
          align: 'center',
          verticalAlign: 'middle',
        },
      });
    }
  });

  return {
    option: { graphic: { elements: elements.map((el) => ({ ...el, silent: true })) } },
    width: layout.width + 2 * MARGIN,
    height: layout.height + 2 * MARGIN,
  };
}
